/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Col, Space } from 'antd';
import intentActions from '@store/actions/intents';
import { editableIntentContent, deleteContent } from '@store/reducers/intents';
import { stringNumberToInteger } from '@utils/format/text';
import SVGIconEdit from '@svg/IconEdit';
import SVGIconDelete from '@svg/IconDelete';
import { borders, colors, sizes } from '@styles/theme';

const IntentNewContent = ({ inputName, inputReference, domainLanguage, readOnly }) => {
  const { sentences, tests, answers, tags } = useSelector((state) => state.intents.content);
  const [items, setItems] = useState([]);
  const dispatch = useDispatch();
  const hasItems = items.length > 0;

  const getItemType = (input) => {
    let itemsType = tests;

    if (input === 'sentence') {
      itemsType = sentences;
    } else if (input === 'answer') {
      itemsType = answers;
    }
    return itemsType;
  };

  useEffect(() => {
    setItems(getItemType(inputName));
  }, [sentences, answers, tests]);

  const getContentType = () => `${inputName}s`;

  const deleteItem = (index) => {
    if (index > -1) {
      const itemsUpdated = items.filter((el, i) => i !== index);
      setItems(itemsUpdated);
      const contentType = getContentType();
      dispatch(deleteContent({ index, contentType }));
      if (contentType === 'sentences') {
        dispatch(intentActions.computeQuality(itemsUpdated, domainLanguage));
      }
    }
  };

  const handleDelete = (event) => {
    event.preventDefault();
    const index = stringNumberToInteger(event.target.id);
    deleteItem(index);
  };

  const handleEdit = (event) => {
    const index = stringNumberToInteger(event.target.id);
    if (Number.isNaN(index)) return;
    const itemText = index >= 0 ? items[index] : '';
    const editions = {
      sentence: inputName === 'sentence' ? itemText : '',
      test: inputName === 'test' ? itemText : '',
      answer: inputName === 'answer' ? itemText : '',
      tags: inputName === 'answer' ? tags[index] : []
    };
    dispatch(editableIntentContent(editions));
    inputReference.focus();
    deleteItem(index);
  };

  const ItemEditable = ({ readOnly }) => {
    return items.map((item, index) => {
      return (
        item && (
          <li
            className="flex space-between align-middle"
            key={`${inputName}-${index}`}
            style={{
              background: colors.alabaster,
              border: borders.midGrey,
              minHeight: sizes.common,
              padding: `${sizes.mili} ${sizes.base}`
            }}
          >
            <Col flex={2}>
              <p>
                {item}
                {inputName === 'answer' && tags[index] && tags[index].length > 0 && (
                  <span
                    style={{
                      color: colors.darkGrey,
                      fontSize: sizes.deci
                    }}
                  >
                    {` (${tags[index] ? tags[index] : ''})`}
                  </span>
                )}
              </p>
            </Col>
            {!readOnly && (
              <Space size="small">
                <Button
                  size="small"
                  type="ghost"
                  icon={<SVGIconEdit id={index} width={sizes.kilo} height={sizes.kilo} style={{ padding: '2px' }} />}
                  style={{ border: 'none' }}
                  onClick={handleEdit}
                />
                <Button
                  size="small"
                  type="ghost"
                  icon={<SVGIconDelete id={index} width={sizes.kilo} height={sizes.kilo} style={{ padding: '2px' }} />}
                  style={{ border: 'none' }}
                  onClick={handleDelete}
                />
              </Space>
            )}
          </li>
        )
      );
    });
  };

  return (
    hasItems && (
      <ul
        className="editable-list"
        style={{
          marginBottom: sizes.common,
          padding: 0
        }}
      >
        <ItemEditable readOnly={readOnly} />
      </ul>
    )
  );
};

IntentNewContent.propTypes = {
  inputName: PropTypes.string,
  inputReference: PropTypes.object,
  domainLanguage: PropTypes.string,
  readOnly: PropTypes.bool
};

export { IntentNewContent };
